import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const LABELS = {
  create: 'Create run', rename: 'Rename run', delete: 'Delete run', commit: 'Commit run',
  uncommit: 'Uncommit run', close: 'Close run', start: 'Start run', unstart: 'Unstart run',
  assign: 'Assign driver', unassign: 'Unassign driver', notify: 'Notify driver',
  add: 'Add stop', remove: 'Remove stop', reorder: 'Reorder stops',
  repair: 'Repair reservation', discard: 'Discard offline proposal',
};

function CommandNotice({ item, commands, isCurrent }) {
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const active = useRef(false);
  const duplicateRisk = item.duplicateRisk || item.kind === 'create' || item.kind === 'notify';
  const invoke = async (action) => {
    if (active.current || !isCurrent()) return;
    active.current = true;
    setBusy(true);
    setActionFailed(false);
    try { if (await action() === false && isCurrent()) setActionFailed(true); }
    catch { if (isCurrent()) setActionFailed(true); }
    finally { active.current = false; setBusy(false); }
  };
  const returnToScope = item.canReview === false;
  const savedMessage = item.refreshState === 'failed'
    ? 'Your change was saved, but the display could not update.'
    : 'Your change was saved. Refresh the display before making another change.';
  return (
    <section className="space-y-2 text-sm" aria-label={LABELS[item.kind] || 'Run change'}>
      <p className="font-semibold">{LABELS[item.kind] || 'Run command'}{item.name ? `: ${item.name}` : ''}</p>
      {item.date && <p>{item.date}</p>}
      <p role="status">
        {item.state === 'accepted'
          ? `${savedMessage}${item.source && item.source !== 'cloud' ? ' This change is saved on this computer, not confirmed online.' : ''}`
          : item.state === 'pending'
            ? 'This change is taking longer than usual. Please wait before trying again.'
            : 'We could not confirm this change. Check the run before trying again.'}
      </p>
      {returnToScope && <p>Return to {item.date} {item.source === 'cloud' ? 'online' : 'offline'} to finish checking this change.</p>}
      {!returnToScope && item.review && <p>{item.canAcknowledge
        ? 'The latest run details are available. Review them before continuing.'
        : 'The run could not be checked. Please try again.'}</p>}
      {actionFailed && <p role="alert">The display could not be updated. Your change was not sent again.</p>}
      {item.state === 'accepted' && !returnToScope && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => invoke(() => commands.refresh(item.id))}>Refresh display</Button>
      )}
      {item.state === 'unknown' && !returnToScope && (
        <div className="mt-2 space-y-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => invoke(() => commands.review(item.id))}>Check run</Button>
          {duplicateRisk && (
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={allowDuplicate} onChange={(event) => { if (isCurrent()) setAllowDuplicate(event.target.checked); }} />
              <span>{item.kind === 'notify'
                ? 'I understand a deliberate resend may notify the driver twice.'
                : 'I understand creating another run may duplicate the earlier request.'}</span>
            </label>
          )}
          <Button size="sm" variant="outline" disabled={busy || !item.canAcknowledge || (duplicateRisk && !allowDuplicate)}
            onClick={() => invoke(() => commands.acknowledge(item.id, { allowDuplicate }))}>
            Finish review
          </Button>
          <p>Finishing this review does not repeat your change. Review the run before taking another action.</p>
        </div>
      )}
    </section>
  );
}

export default function RunCommandStatus({ commands }) {
  const scope = `run-feedback-${useId()}`;
  const sequence = useRef(0);
  const activeToast = useRef(null);
  const ownedToasts = useRef(new Set());
  const live = useRef(false);
  const latest = useRef(null);
  const [reopen, setReopen] = useState(0);
  const issues = (commands?.pending || []).filter(item => item.state === 'unknown'
    || (item.state === 'pending' && item.stalled)
    || (item.state === 'accepted' && ['failed', 'needed'].includes(item.refreshState)));
  const signature = JSON.stringify(issues.map(item => [item.id, item.kind, item.state, item.refreshState,
    item.date, item.name, item.source, item.review, item.canAcknowledge, item.duplicateRisk, item.canReview]));
  latest.current = { commands, signature, issues };
  useEffect(() => {
    live.current = true;
    const owned = ownedToasts.current;
    return () => {
      live.current = false; activeToast.current = null;
      owned.forEach(id => toast.dismiss(id)); owned.clear();
    };
  }, []);
  useEffect(() => {
    if (!latest.current.issues.length) {
      if (activeToast.current) {
        toast.dismiss(activeToast.current); ownedToasts.current.delete(activeToast.current);
      }
      activeToast.current = null;
      return;
    }
    // Closing feedback never releases command custody. Reopening gets a fresh
    // identity so an older Sonner exit animation cannot erase the new notice.
    const id = activeToast.current || `${scope}-${++sequence.current}`;
    activeToast.current = id; ownedToasts.current.add(id);
    const isCurrent = () => live.current && activeToast.current === id
      && latest.current.signature === signature && latest.current.commands?.isCurrent?.() === true;
    const actions = Object.fromEntries(['refresh', 'review', 'acknowledge'].map(name =>
      [name, (...args) => latest.current.commands[name](...args)]));
    toast.error('Delivery needs attention', {
      id, duration: Infinity, closeButton: true,
      onDismiss: () => {
        if (activeToast.current === id) activeToast.current = null;
        ownedToasts.current.delete(id);
      },
      description: <div className="max-h-[60vh] overflow-y-auto space-y-4" data-testid="run-recovery">
        {latest.current.issues.map(item => <CommandNotice key={item.id} item={item} commands={actions} isCurrent={isCurrent} />)}
      </div>,
    });
    // Callback identity is not new evidence and must not reopen dismissed feedback.
  }, [scope, signature, reopen]);
  return <div className="h-7 w-7 flex-shrink-0" data-testid="run-feedback-slot">
    {issues.length > 0 && <Button size="sm" variant="ghost" className="h-7 w-7 p-1 text-amber-700"
      aria-label="Delivery needs attention" title="Delivery needs attention"
      onClick={() => { if (commands?.isCurrent?.()) setReopen(value => value + 1); }}>
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
    </Button>}
  </div>;
}
